/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Tooltip } from 'uikit/tooltips';

import Button from './button';

import styles from '../styles/uikitButtonsViewToggle.module.scss';

const ViewToggle = ({
  buttons = [],
  className = '',
  defaultValue = '',
  isDisabled = false,
  selectedValue = '',
  testId = '',
  variant = 'default',
  ...other
}) => {
  if (!buttons?.length) return null;

  const initialValidButtons = buttons.reduce((acc, button) => {
    const { icon, text } = button;
    if (!icon && !text) {
      console.warn('ViewToggle: Button not rendered. Must have text or icon');
      return acc;
    }
    acc.push(button);
    return acc;
  }, []);

  let validButtons = [];
  const isAllIcons = initialValidButtons.every((button) => button.icon);
  if (isAllIcons) {
    validButtons = initialValidButtons;
  } else {
    validButtons = initialValidButtons.filter((button) => button.text);
  }

  if (validButtons.length < 2) {
    console.warn('ViewToggle: Must have at least two valid buttons');
    return null;
  }

  const buttonKeys = validButtons.map((button) => button.key);
  const trueDefaultValue =
    defaultValue?.length > 0 && buttonKeys.includes(defaultValue) ? defaultValue : buttonKeys[0];
  const validSelectedValue = buttonKeys.includes(selectedValue) ? selectedValue : trueDefaultValue;

  const buttonComponents = validButtons.map((button) => {
    const { ariaLabel, icon, key, text, tooltip, ...buttonProps } = button;
    const buttonIcon = isAllIcons && icon ? icon : '';
    const buttonText = buttonIcon ? '' : text;
    const isValidTooltip = tooltip && tooltip.content;

    const getAriaLabel = () => {
      if (buttonIcon) {
        return ariaLabel || text;
      }
      return null;
    };

    const isButtonSelected = validSelectedValue === button.key;

    const buttonComponent = (
      <Button
        {...buttonProps}
        ariaLabel={getAriaLabel()}
        className={isButtonSelected ? styles.isSelected : ''}
        icon={buttonIcon}
        isDisabled={isDisabled || button.isDisabled}
        isSelected={isButtonSelected}
        key={!isValidTooltip && key}
        size="small"
        text={buttonText}
        variant={buttonIcon ? 'toggleIcon' : 'toggleText'}
        aria-pressed={isButtonSelected}
      />
    );

    if (isValidTooltip) {
      return (
        <Tooltip {...tooltip} key={key}>
          {buttonComponent}
        </Tooltip>
      );
    }
    return buttonComponent;
  });

  return (
    <div
      className={classnames(
        styles.element,
        variant === 'admin' && styles.isVariantAdmin,
        className,
      )}
      role="group"
      data-testid={testId}
      {...other}
    >
      {buttonComponents}
    </div>
  );
};

ViewToggle.propTypes = {
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      ariaControls: PropTypes.string,
      ariaExpanded: PropTypes.bool,
      ariaLabel: PropTypes.string,
      className: PropTypes.string,
      icon: PropTypes.element,
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      isDisabled: PropTypes.bool,
      key: PropTypes.string,
      onClick: PropTypes.func,
      testId: PropTypes.string,
      text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      to: PropTypes.string,
      tooltip: PropTypes.shape({
        className: PropTypes.string,
        content: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func])
          .isRequired,
        delay: PropTypes.arrayOf(PropTypes.number),
        duration: PropTypes.arrayOf(PropTypes.number),
        placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
        zIndex: PropTypes.number,
      }),
    }),
  ),
  className: PropTypes.string,
  defaultValue: PropTypes.string,
  isDisabled: PropTypes.bool,
  selectedValue: PropTypes.string,
  testId: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'admin']),
};

export { ViewToggle as ViewToggleStorybook };
export default React.memo(ViewToggle);
